import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText,
  ChevronRight,
  ChevronDown,
  Plus,
  Filter,
  CheckCircle2,
  Circle,
  FolderOpen,
  Search
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stages = [
  { id: "new", label: "New", color: "bg-slate-600" },
  { id: "fact_finding", label: "Fact Finding", color: "bg-blue-600" },
  { id: "documents", label: "Documents", color: "bg-indigo-600" },
  { id: "research", label: "Research", color: "bg-purple-600" },
  { id: "compliance", label: "Compliance", color: "bg-pink-600" },
  { id: "lender_submission", label: "Lender Submission", color: "bg-cyan-600" },
  { id: "survey", label: "Survey", color: "bg-teal-600" },
  { id: "offer", label: "Offer", color: "bg-amber-600" },
  { id: "solicitors", label: "Solicitors", color: "bg-orange-600" },
  { id: "legal_completion", label: "Legal Completion", color: "bg-rose-600" },
  { id: "protection", label: "Protection", color: "bg-emerald-600" },
  { id: "completed", label: "Completed", color: "bg-green-600" }
];

const stageProgress = {
  new: 5, fact_finding: 15, documents: 25, research: 35, compliance: 45,
  lender_submission: 55, survey: 65, offer: 75, solicitors: 82, 
  legal_completion: 90, protection: 95, completed: 100
};

export default function ApplicationTracker() {
  const { user, getAuthHeaders } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedApp, setExpandedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newAppForm, setNewAppForm] = useState({
    client_name: "", client_email: "", property_value: "", loan_amount: "", mortgage_type: "residential"
  });

  const isStaff = ["admin", "advisor"].includes(user?.role);

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  const fetchApplications = async () => {
    try {
      let url = `${API}/applications`;
      if (filter !== "all") url += `?stage=${filter}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
    setLoading(false);
  };

  const createApplication = async () => {
    try {
      await axios.post(`${API}/applications`, {
        ...newAppForm,
        client_id: user.id,
        property_value: parseFloat(newAppForm.property_value),
        loan_amount: parseFloat(newAppForm.loan_amount)
      }, { headers: getAuthHeaders() });
      toast.success("Application created successfully!");
      setDialogOpen(false);
      setNewAppForm({ client_name: "", client_email: "", property_value: "", loan_amount: "", mortgage_type: "residential" });
      fetchApplications();
    } catch (error) {
      toast.error("Failed to create application");
    }
  };

  const updateStage = async (appId, newStage) => {
    try {
      await axios.patch(`${API}/applications/${appId}/stage`, { stage: newStage }, { headers: getAuthHeaders() });
      toast.success("Stage updated!");
      fetchApplications();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const updateSubTask = async (appId, subTaskId, completed) => {
    try {
      await axios.patch(`${API}/applications/${appId}/sub-task`, { 
        sub_task_id: subTaskId, 
        completed 
      }, { headers: getAuthHeaders() });
      toast.success("Sub-task updated!");
      fetchApplications();
    } catch (error) {
      toast.error("Failed to update sub-task");
    }
  };

  const getSubTaskCompletion = (subTasks) => {
    if (!subTasks || subTasks.length === 0) return 0;
    const completed = subTasks.filter(t => t.completed).length;
    return Math.round((completed / subTasks.length) * 100);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="application-tracker">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold mb-2">Application Tracker</h1>
            <p className="text-slate-400">Track and manage mortgage applications through every stage.</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px] bg-[#0f172a] border-slate-700" data-testid="stage-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by stage" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-700 max-h-80">
                <SelectItem value="all">All Stages</SelectItem>
                {stages.map(stage => (
                  <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isStaff && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-application-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Application
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0f172a] border-slate-700">
                  <DialogHeader>
                    <DialogTitle>Create New Application</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Client Name</Label>
                      <Input
                        value={newAppForm.client_name}
                        onChange={(e) => setNewAppForm({ ...newAppForm, client_name: e.target.value })}
                        className="bg-[#020617] border-slate-700"
                        data-testid="new-app-client-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Client Email</Label>
                      <Input
                        type="email"
                        value={newAppForm.client_email}
                        onChange={(e) => setNewAppForm({ ...newAppForm, client_email: e.target.value })}
                        className="bg-[#020617] border-slate-700"
                        data-testid="new-app-client-email"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Property Value (£)</Label>
                        <Input
                          type="number"
                          value={newAppForm.property_value}
                          onChange={(e) => setNewAppForm({ ...newAppForm, property_value: e.target.value })}
                          className="bg-[#020617] border-slate-700"
                          data-testid="new-app-property-value"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Loan Amount (£)</Label>
                        <Input
                          type="number"
                          value={newAppForm.loan_amount}
                          onChange={(e) => setNewAppForm({ ...newAppForm, loan_amount: e.target.value })}
                          className="bg-[#020617] border-slate-700"
                          data-testid="new-app-loan-amount"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Mortgage Type</Label>
                      <Select value={newAppForm.mortgage_type} onValueChange={(v) => setNewAppForm({ ...newAppForm, mortgage_type: v })}>
                        <SelectTrigger className="bg-[#020617] border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-slate-700">
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="buy_to_let">Buy to Let</SelectItem>
                          <SelectItem value="remortgage">Remortgage</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="first_time_buyer">First Time Buyer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={createApplication} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-new-app">
                      Create Application
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Stage Pipeline */}
        <Card className="bg-[#0f172a] border-slate-800 mb-8 overflow-x-auto">
          <CardContent className="p-4">
            <div className="flex gap-1 min-w-max">
              {stages.map((stage, index) => {
                const count = applications.filter(a => a.stage === stage.id).length;
                return (
                  <div key={stage.id} className="flex items-center">
                    <div 
                      className={`px-3 py-2 rounded cursor-pointer transition-all text-center min-w-[80px] ${
                        filter === stage.id ? 'ring-2 ring-blue-500' : ''
                      } ${stage.color}`}
                      onClick={() => setFilter(filter === stage.id ? 'all' : stage.id)}
                    >
                      <p className="text-[10px] text-white/80 truncate">{stage.label}</p>
                      <p className="text-lg font-bold text-white">{count}</p>
                    </div>
                    {index < stages.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-slate-600 mx-0.5 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Applications List */}
        <div className="space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-slate-800 animate-pulse rounded-lg"></div>
              ))}
            </div>
          ) : applications.length === 0 ? (
            <Card className="bg-[#0f172a] border-slate-800">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No applications found</p>
              </CardContent>
            </Card>
          ) : (
            applications.map((app) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-[#0f172a] border-slate-800 hover:border-slate-700 transition-colors">
                  <CardContent className="p-6">
                    {/* Main Info */}
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{app.client_name}</h3>
                          <Badge className={`${stages.find(s => s.id === app.stage)?.color} text-white`}>
                            {stages.find(s => s.id === app.stage)?.label}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400">Property Value</p>
                            <p className="font-mono">£{app.property_value?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Loan Amount</p>
                            <p className="font-mono">£{app.loan_amount?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Type</p>
                            <p className="capitalize">{app.mortgage_type?.replace(/_/g, ' ')}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Created</p>
                            <p>{new Date(app.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-slate-400 mb-1">
                            <span>Overall Progress</span>
                            <span>{stageProgress[app.stage]}%</span>
                          </div>
                          <Progress value={stageProgress[app.stage]} className="h-2" />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {isStaff && (
                          <Select
                            value={app.stage}
                            onValueChange={(value) => updateStage(app.id, value)}
                          >
                            <SelectTrigger className="w-[180px] bg-[#020617] border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0f172a] border-slate-700 max-h-60">
                              {stages.map(stage => (
                                <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                          className="border-slate-700"
                        >
                          {expandedApp === app.id ? 'Hide' : 'View'} Sub-Tasks
                          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${expandedApp === app.id ? 'rotate-180' : ''}`} />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Sub-Tasks */}
                    {expandedApp === app.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-6 pt-6 border-t border-slate-800"
                      >
                        <Tabs defaultValue="documents" className="w-full">
                          <TabsList className="bg-[#020617] mb-4">
                            <TabsTrigger value="documents" className="data-[state=active]:bg-blue-600">
                              <FolderOpen className="w-4 h-4 mr-2" />
                              Documents ({getSubTaskCompletion(app.document_sub_tasks)}%)
                            </TabsTrigger>
                            <TabsTrigger value="research" className="data-[state=active]:bg-blue-600">
                              <Search className="w-4 h-4 mr-2" />
                              Research ({getSubTaskCompletion(app.research_sub_tasks)}%)
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent value="documents">
                            <div className="grid md:grid-cols-2 gap-3">
                              {app.document_sub_tasks?.map((task) => (
                                <div
                                  key={task.id}
                                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                                    task.completed 
                                      ? 'bg-green-500/10 border-green-500/30' 
                                      : 'bg-[#020617] border-slate-800'
                                  }`}
                                >
                                  {isStaff ? (
                                    <Checkbox
                                      checked={task.completed}
                                      onCheckedChange={(checked) => updateSubTask(app.id, task.id, checked)}
                                    />
                                  ) : (
                                    task.completed ? (
                                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : (
                                      <Circle className="w-5 h-5 text-slate-500" />
                                    )
                                  )}
                                  <div className="flex-1">
                                    <p className={`text-sm ${task.completed ? 'line-through text-slate-500' : ''}`}>
                                      {task.name}
                                    </p>
                                    {task.required && !task.completed && (
                                      <span className="text-xs text-amber-500">Required</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>

                          <TabsContent value="research">
                            <div className="grid md:grid-cols-2 gap-3">
                              {app.research_sub_tasks?.map((task) => (
                                <div
                                  key={task.id}
                                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                                    task.completed 
                                      ? 'bg-green-500/10 border-green-500/30' 
                                      : 'bg-[#020617] border-slate-800'
                                  }`}
                                >
                                  {isStaff ? (
                                    <Checkbox
                                      checked={task.completed}
                                      onCheckedChange={(checked) => updateSubTask(app.id, task.id, checked)}
                                    />
                                  ) : (
                                    task.completed ? (
                                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : (
                                      <Circle className="w-5 h-5 text-slate-500" />
                                    )
                                  )}
                                  <div className="flex-1">
                                    <p className={`text-sm ${task.completed ? 'line-through text-slate-500' : ''}`}>
                                      {task.name}
                                    </p>
                                    {task.required && !task.completed && (
                                      <span className="text-xs text-amber-500">Required</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                        </Tabs>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
